import { Injectable } from '@angular/core';

export interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Lax' | 'Strict' | 'None';
}

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  /**
   * Check if cookies are available in the browser
   */
  check(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    try {
      const testCookie = '__cookie_test__';
      this.set(testCookie, 'test', { path: '/' });
      const cookieExists = this.get(testCookie) === 'test';
      this.remove(testCookie, { path: '/' });
      return cookieExists;
    } catch (_) {
      return false;
    }
  }

  /**
   * Get a cookie value by name
   */
  get(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i] ?? '';
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Get all cookies as an object
   */
  getAll(): { [key: string]: string } {
    if (typeof document === 'undefined') {
      return {};
    }

    const cookies: { [key: string]: string } = {};
    const cookieArray = document.cookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
      const cookie = (cookieArray[i] ?? '').trim();
      const [name, value] = cookie.split('=');
      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value);
      }
    }
    return cookies;
  }

  /**
   * Set a cookie
   * @param name Cookie name
   * @param value Cookie value
   * @param options Cookie options (expires, path, domain, secure, sameSite)
   */
  set(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof document === 'undefined') {
      return;
    }

    const { expires, path = '/', domain, secure, sameSite } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (expires) {
      let expiresDate: Date;
      if (typeof expires === 'number') {
        expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + expires * 24 * 60 * 60 * 1000);
      } else {
        expiresDate = expires;
      }
      cookieString += `; expires=${expiresDate.toUTCString()}`;
    }

    if (path) {
      cookieString += `; path=${path}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; secure';
    }

    if (sameSite) {
      cookieString += `; samesite=${sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Set a session cookie (expires when browser is closed)
   * @param name Cookie name
   * @param value Cookie value
   * @param options Cookie options (path, domain, secure, sameSite) - expires will be ignored
   */
  setSession(name: string, value: string, options: Omit<CookieOptions, 'expires'> = {}): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Session cookies are set without the expires attribute
    this.set(name, value, { ...options, expires: undefined });
  }

  /**
   * Remove a cookie
   * @param name Cookie name
   * @param options Cookie options (path, domain) - should match the options used when setting the cookie
   */
  remove(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Set the cookie with an expiration date in the past
    const pastDate = new Date(0);
    this.set(name, '', { ...options, expires: pastDate });
  }

  /**
   * Remove all cookies
   * Note: This may not work for cookies with specific path or domain attributes
   */
  removeAll(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const cookies = this.getAll();
    for (const name in cookies) {
      if (Object.prototype.hasOwnProperty.call(cookies, name)) {
        this.remove(name, { path: '/' });
        this.remove(name); // Also try without path
      }
    }
  }
}
